import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Injector, OnDestroy, ChangeDetectionStrategy, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, of, timer } from 'rxjs';
import { catchError, delay, filter, map, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { LogRecord } from '../../../common/model/log-record';
import { ProfileService } from '../../../common/service/data/profile.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { SelectionItem } from '../../../core/component/select/select.component';
import { dateTimeString } from '../../../core/helper/date-time';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, standardCommand } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LanguageService } from '../../../core/service/language.service';
import { FaultNotification } from '../../../fault-notification/model/faultNotification';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { B2CSubscriptionContact } from '../../../invoice/model/b2c/b2c-subscription-contact';
import { B2CSubscriptionSuggestion } from '../../../invoice/model/b2c/b2c-subscription-suggestion';
import { InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { B2CSubscriptionContactService, IB2CContactToggleResult } from '../../../invoice/service/data/b2c-subscription-contact.service';
import { tenantCommand } from '../../model/const/command';
import { LeaseAgreementCategory, TenantCategory, TenantCategoryToString } from '../../model/const/enums';
import { B2CContactSaveAction, Contact, ContactIdKind } from '../../model/contact';
import { Contract } from '../../model/contract';
import { IdType } from '../../model/id-type';
import { RentApplication } from '../../model/rent-application';
import { PrintGdprCommandHandler } from '../../service/command/print-gdpr-command-handler.service';
import { RepayBalanceCommandHandler } from '../../service/command/repay-balance-command-handler.service';
import { SendRegistrationKeyCommandHandler } from '../../service/command/send-registration-key-command-handler.service';
import { TenantFormCommandHandlerResolver } from '../../service/command/tenant-form-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { ContactDocumentService } from '../../service/data/contact-document.service';
import { ContactLogService } from '../../service/data/contact-log.service';
import { ContactService } from '../../service/data/contact.service';
import { ContactDocumentFormComponent } from '../contact-document-form/contact-document-form.component';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { SidebarService } from '../../../core/service/sidebar.service';
import { ProtectedIdentityService } from '../../service/data/protected-identity.service';
import { ProtectedIdentityBankIdDialogComponent } from '../protected-identity-bankid-dialog/protected-identity-bankid-dialog.component';

function formatPersonalIdNo(ssn: string): string {
  if (!ssn) {
    return '';
  } else if (ssn.length === 12 || ssn.length === 10) {
    // (with || without century)
    return `${ssn.substring(0, ssn.length - 4)}-${ssn.substring(ssn.length - 4)}`;
  } else {
    return ssn; // not sure: return as-is
  }
}

@Component({
  selector: 'app-tenant-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
  providers: [
    DeleteFormCommandHandler,
    NavigationService,
    SendRegistrationKeyCommandHandler,
    TenantFormCommandHandlerResolver,
    PrintGdprCommandHandler,
    RepayBalanceCommandHandler,
    { provide: DataService, useExisting: ContactService },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ContactFormComponent extends FormComponentBase<Contact> implements OnDestroy {
  @ViewChild('protectedIdentitySidebarTemplate') protectedIdentitySidebarTemplate: TemplateRef<unknown>;
  private hasProtectedIdentityFeature = false;
  protectedIdentitySidebarContactId?: string;

  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _cf: FormControlExFactory,
    private fb: FormBuilder,
    private _profileService: ProfileService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    dispatcher: CommandDispatcher,
    handlerResolver: TenantFormCommandHandlerResolver,
    private _dataService: ContactService,
    private _messageService: MessageService,
    navigation: NavigationService,
    protected faultNotificationService: FaultNotificationService,
    columnDefinitionService: ColumnDefinitionService,
    private documentService: ContactDocumentService,
    private _actionWindowService: ActionWindowService,
    private _injector: Injector,
    private _nativeWindow: NativeWindowService,
    private _b2cContactService: B2CSubscriptionContactService,
    private _languageService: LanguageService,
    private logService: ContactLogService,
    private _clipboard: Clipboard,
    private readonly sidebarService: SidebarService,
    private readonly protectedIdentityService: ProtectedIdentityService,
    userAccount: UserAccountService
  ) {
    super(activatedRoute, _dataService, _messageService, navigation, dispatcher, handlerResolver);

    this.setDefaults = true;

    this.AddSubscription(
      this.activatedRoute.paramMap
        .pipe(
          map(params => params.get('id')),
          pairwise(),
          filter(([previousId, currentId]) => previousId !== currentId)
        )
        .subscribe(() => this.closeProtectedIdentitySidebar())
    );

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          this.hasProtectedIdentityFeature = account.hasProtectedIdentityAccess();
          this.addDeleteMenu = false;
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('TENANT.COMMAND.SEND_REGISTRATION_KEY', tenantCommand.sendRegistrationKey);
          }
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('TENANT.COMMAND.CREATE_FAULT_NOTIFICATION', tenantCommand.showFaultNotification);
          }
          this.addMenuItemDefinition('TENANT.COMMAND.PRINT_GDPR', tenantCommand.printGDPR);
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('TENANT.COMMAND.CLEANUP_DATA', tenantCommand.cleanupData);
          }
          this.addMenuItemDefinition('TENANT.COMMAND.SHOW_INVOICE', tenantCommand.showInvoices);
          this.syncProtectedIdentityMenu();
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('TENANT.COMMAND.REPAY_BALANCE', tenantCommand.repayBalance);
          }
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('TENANT.COMMAND.MOVE_BALANCE', tenantCommand.moveBalance);
          }
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
          }
          if (account.hasContactWriteAccess()) {
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (
            account.hasAccess(ModuleNames.Common, Features.LinkContactDocuments) ||
            account.hasAccess(ModuleNames.Common, Features.EditContact) ||
            account.hasAccess(ModuleNames.Rent, Features.ManageLeaseAgreements)
          ) {
            this.allowLinkContactDocument = true;
          }
        },
      })
    );

    this.leaseAgreementColumnDefinition = [
      this._cdf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cdf.create('TENANT.LEASE_AGREEMENT_CONNECTION_TYPE', 'contactLeaseAgreementConnectionTypes'),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cdf.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalObjectRentalId', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._cdf.createDate('LEASE_AGREEMENT.AGREEMENT_DATE', 'agreementDate', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.RENTAL_OBJECT', 'rentalObject'),
      this._cdf.create('LEASE_AGREEMENT.RENTAL_OBJECT_TYPE', 'rentalObjectType'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwoName', ColumnVisibility.optional),
      this._cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      this._cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      this._cdf.create('PROPERTY.NUMBER', 'propertyNumber', ColumnVisibility.optional),
      this._cdf.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', 'invoiceDeliveryMethodText'),
      this._cdf.create('INVOICE.PREVIEW.INVOICE_ADDRESS', 'invoiceAddressString'),
      this._cdf.createBoolean('LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS', 'individualInvoiceAddress', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
      this._cdf.createTranslation('LEASE_AGREEMENT.DIRECT_DEBIT', 'directDebitStatusCaption', ColumnVisibility.optional),
      this._cdf.createDate('LEASE_AGREEMENT_NOTICE_PERIOD_FORM.TO_DATE_LABEL', 'toDate', ColumnVisibility.optional),
    ];

    this.brfMembershipColumnDefinition = [
      this._cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.mandatory),
      this._cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.mandatory),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_FROM', 'fromDate', ColumnVisibility.mandatory),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_TO', 'toDate', ColumnVisibility.mandatory),
      this._cdf.createDate('COOPERATIVE_HOUSING.MEMBER_APPROVAL_DATE', 'approvalDate', ColumnVisibility.mandatory),
    ];

    this.rentApplicationsColumnDefinition = [
      this._cdf.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._cdf.createTranslation('RENT_APPLICATION.OBJECT_CATEGORY', 'rentalObjectCategoryCaption', ColumnVisibility.default),
      this._cdf.create('RENT_APPLICATION.MIN_ROOMS', 'numberOfRoomsMin', ColumnVisibility.default),
      this._cdf.create('RENT_APPLICATION.MAX_ROOMS', 'numberOfRoomsMax', ColumnVisibility.default),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];

    this.contactBalanceTransactionColumnDefinition = [
      this._cdf.create('COMMON.COMPANY', 'companyNumberAndName'),
      this._cdf.createDate('INCORRECT_PAYMENT.CREATION_DATE', 'createdDate'),
      this._cdf.createMoney('COMMON.AMOUNT', 'amount'),
      this._cdf.create('ACCOUNT_TRANSACTION.DESCRIPTION', 'description'),
      this._cdf.createDate('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate'),
      this._cdf.create('VERIFICATION.NUMBER', 'verificationNumber'),
      this._cdf.create('CONTACT_BALANCE_TRANSACTION.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
    ];

    this.contactPaymentInformationPerCompanyColumnDefinition = [
      this._cdf.create('COMMON.STATUS', 'directDebitStatus'),
      this._cdf.create('DIRECT_DEBIT.PAYER_NAME', 'payerNumber'),
      this._cdf.create('COMMON.COMPANY', 'companyNumberName'),
      this._cdf.create('COMPANY.BANK_ACCOUNT.BANKGIRO_NUMBER', 'bankGiroNumberFormatted'),
      this._cdf.createDate('DIRECT_DEBIT.CREATED_DATE', 'directDebitFromDate'),
      this._cdf.createDate('DIRECT_DEBIT_INFORMATION.REQUEST_DATE', 'directDebitRequestDate'),
      this._cdf.createDate('DIRECT_DEBIT_INFORMATION.CONFIRM_DATE', 'directDebitConfirmDate'),
      this._cdf.createDate('DIRECT_DEBIT_INFORMATION.REQUEST_FROM_INTERNET_BANK', 'requestFromInternetBankDate'),
      this._cdf.createDate('DIRECT_DEBIT_INFORMATION.RESPONSE_TO_INTERNET_BANK_SENT', 'responseToInternetBankSentDate'),
      this._cdf.createDate('DIRECT_DEBIT_INFORMATION.CANCEL_CONFIRM_DATE', 'directDebitCancelConfirmDate'),
    ];

    this.b2CSubscriptionContactColumnDefinition = [
      this._cdf.create('B2C_SUBS.ISSUER_IDENTITY', 'issuerIdentity'),
      this._cdf.create('TENANT.EINVOICE_ADDRESS_B2C', 'recipientIdentity'),
      this._cdf.createTranslation('B2C_SUBS.CURRENT_STATE', 'currentStateCaption'),
      this._cdf.createTranslation('B2C_SUBS.IMPORT_EVENT.STATE_CHANGE', 'stateChangeCaption'),
      this._cdf.createDateTime('B2C_SUBS.IMPORT_EVENT.STATE_CHANGED_DATE', 'stateChangedDate'),
    ];

    this.documentsColumnDefinition = [
      this._cdf.create('DOCUMENT.CAPTION', 'caption'),
      this._cdf.createDateTime('DOCUMENT.UPLOADED_DATE', 'uploadedDate'),
      this._cdf.create('DOCUMENT.SIZE', 'size', ColumnVisibility.optional),
      this._cdf.createTranslation('DOCUMENT.DOCUMENT_TYPE', 'connectionTypeText', ColumnVisibility.default),
    ];
    this.documentsContextMenuDefinitions = [
      new MenuItemDefinition('DOWNLOAD_BTN', tenantCommand.downloadDocument),
      new MenuItemDefinition('DELETE_BTN', tenantCommand.deleteDocument),
    ];

    this.faultNotificationsColumnDefinition = columnDefinitionService.getRentalObjectFaultNotificationsColumnDefinition();
    this.logColumnDefinition = columnDefinitionService.create('Log');
  }

  allowLinkContactDocument: boolean;

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case tenantCommand.protectedIdentity:
        return this.hasProtectedIdentityFeature && this.isExistingContact;
      case tenantCommand.deleteDocument:
      case tenantCommand.downloadDocument:
        return command instanceof Command && command.context;

      default:
        return super.canExecute(command);
    }
  }

  execute(command: CommandBase): void {
    if (command instanceof Command) {
      switch (command.id) {
        case tenantCommand.protectedIdentity:
          this.openProtectedIdentitySidebar();
          return;
        case tenantCommand.deleteDocument:
          this.AddSubscription(
            this.documentService
              .delete(command.context.id)
              .pipe(switchMap(() => this.documentService.getByOwnerId(this.dataContext.id)))
              .subscribe(res => (this.dataContext.documents = res))
          );
          return;
        case tenantCommand.downloadDocument:
          this.AddSubscription(
            this.documentService.download((<Command>command).context).subscribe(res => {
              this._nativeWindow.save(res);
            })
          );
          return;
      }
    }
    super.execute(command);
  }

  closeProtectedIdentitySidebar(): void {
    if (this.sidebarService.hasSidebarContent(this.protectedIdentitySidebarTemplate)) {
      this.sidebarService.toggleSidebar(false);
    }
    this.protectedIdentitySidebarContactId = undefined;
  }

  private openProtectedIdentitySidebar(): void {
    if (
      !this.hasProtectedIdentityFeature ||
      !this.dataContext?.id ||
      !this.protectedIdentitySidebarTemplate ||
      !this.isPerson
    ) {
      return;
    }

    const contactId = this.dataContext.id;
    this.AddSubscription(
      this.protectedIdentityService
        .getAccess()
        .pipe(
          switchMap(access =>
            access.hasAccess
              ? of(access)
              : this._actionWindowService.open(
                  ProtectedIdentityBankIdDialogComponent,
                  this._injector,
                  null
                )
          ),
          take(1),
          filter(access => access?.hasAccess === true)
        )
        .subscribe({
          next: () => this.showProtectedIdentitySidebar(contactId),
          error: () => {},
        })
    );
  }

  private showProtectedIdentitySidebar(contactId: string): void {
    if (
      this.dataContext?.id !== contactId ||
      this.activatedRoute.snapshot.paramMap.get('id') !== contactId ||
      !this.protectedIdentitySidebarTemplate
    ) {
      return;
    }

    this.protectedIdentitySidebarContactId = contactId;
    this.sidebarService.setSidebarContent({
      template: this.protectedIdentitySidebarTemplate,
      onClose: () => this.closeProtectedIdentitySidebar(),
      sidebarClass: 'wide-sidebar-protected-data-temporary',
    });
    this.sidebarService.toggleSidebar(true);
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { openProtectedIdentity: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngAfterViewInit(): void {
    if (this.activatedRoute.snapshot.queryParamMap.get('openProtectedIdentity') !== 'true') return;

    if (this.dataContext?.id) {
      this.openProtectedIdentitySidebar();
      return;
    }

    this.AddSubscription(
      this.dataContextInitialized$
        .pipe(
          filter(initialized => initialized === true),
          take(1),
          delay(0)
        )
        .subscribe(() => this.openProtectedIdentitySidebar())
    );
  }

  ngOnDestroy() {
    this.closeProtectedIdentitySidebar();
    super.ngOnDestroy();
    if (this._idSubscription) this._idSubscription.unsubscribe();
  }

  override initializeSection(section: string) {
    switch (section) {
      case 'logs':
        this.AddSubscription(
          this.logService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
            this.logs = res.items;
          })
        );
    }
    super.initializeSection(section);
  }

  b2CSubscriptionsHasText: string = '';
  b2CSubscriptionsSuggestionText: string = '';
  existingContactName: string;
  emailInvoicing: string;

  private _allowDuplicateSSN: boolean = true;
  faultNotificationsLoaded: boolean;
  documentsLoaded: boolean;
  hasActiveB2C: boolean;
  showCode: boolean;
  hasB2B: boolean;
  counterPartUsed: boolean = false;
  webUserExists: boolean = false;
  hasToggledB2C: boolean = false;
  b2cSuggestions: B2CSubscriptionSuggestion[] = [];

  private _idTypes: IdType[];
  private _idSubscription: Subscription;
  logs: LogRecord[] = [];
  idTypeSelection: SelectionItem[];
  b2cSubscriptionContacts: B2CSubscriptionContact[] = [];
  logColumnDefinition: ColumnDefinition[];
  documentsColumnDefinition: ColumnDefinition[];
  leaseAgreementColumnDefinition: ColumnDefinition[];
  brfMembershipColumnDefinition: ColumnDefinition[];
  rentApplicationsColumnDefinition: ColumnDefinition[];
  faultNotificationsColumnDefinition: ColumnDefinition[];
  contactBalanceTransactionColumnDefinition: ColumnDefinition[];
  contactPaymentInformationPerCompanyColumnDefinition: ColumnDefinition[];
  b2CSubscriptionContactColumnDefinition: ColumnDefinition[];
  documentsContextMenuDefinitions: MenuItemDefinition[];

  private _verifyEmailDuplicates: boolean = false;

  categories = [
    { name: TenantCategoryToString(TenantCategory.PhysicalPerson), value: TenantCategory.PhysicalPerson },
    { name: TenantCategoryToString(TenantCategory.Company), value: TenantCategory.Company },
  ];

  private get categoryControl(): FormControlEx {
    return this.form?.controls['category'] as FormControlEx;
  }
  get idType(): ContactIdKind {
    return this.form?.controls['idType']?.value || ContactIdKind.None;
  }
  get isCompany(): boolean {
    let c = this.categoryControl;
    return c ? c.value !== TenantCategory.PhysicalPerson : false;
  }
  get isPerson(): boolean {
    let c = this.categoryControl;
    return c ? c.value === TenantCategory.PhysicalPerson : false;
  }
  get isMappedToB2C(): boolean {
    return !!this.b2cSubscriptionContacts?.length;
  }
  get hasB2C(): boolean {
    return this.isPerson && this.isMappedToB2C;
  }
  get hasB2CSuggestion(): boolean {
    return this.isPerson && !!this.b2cSuggestions?.length;
  }
  get b2cToggleControl(): FormControlEx {
    return this.form?.controls['b2cToggleControl'] as FormControlEx;
  }
  get b2cToggleDefaultValue(): boolean {
    return this.isPerson && this.hasActiveB2C;
  }
  get b2CSubscriptionsText(): string {
    return this.isMappedToB2C ? this.b2CSubscriptionsHasText : this.b2CSubscriptionsSuggestionText;
  }
  get b2cToggleHelpText(): string {
    return this.isMappedToB2C ? 'TENANT.HELP_TEXT.EINVOICE_B2C' : 'TENANT.HELP_TEXT.EINVOICE_B2C_SUGGESTED';
  }

  linkDocument() {
    this.AddSubscription(
      this._actionWindowService
        .open(ContactDocumentFormComponent, this._injector, null, this.dataContext)
        .pipe(switchMap(x => this.documentService.getByOwnerId(this.dataContext.id)))
        .subscribe(res => (this.dataContext.documents = res))
    );
  }

  setDocumentsVisibility(show: boolean) {
    if (this.documentsLoaded) {
      return;
    }
    this.AddSubscription(
      this.documentService.getByOwnerId(this.dataContext.id).subscribe(x => {
        this.dataContext.documents = x;
        this.documentsLoaded = true;
      })
    );
  }

  setFaultNotificationsVisibility(show: boolean) {
    if (this.faultNotificationsLoaded) {
      return;
    }
    this.AddSubscription(
      this.faultNotificationService.getByContact(this.dataContext.id).subscribe(f => {
        this.dataContext.faultNotifications = f;
        this.faultNotificationsLoaded = true;
      })
    );
  }

  onFaultNotificationRowClicked(e: { context: FaultNotification; event: any }): void {
    this.navigation.navigate(['fault-notification', 'notifications', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  eInvoiceAddress(control: AbstractControl): ValidationErrors | null {
    return /^([^:]+:[^:]+)$/.test(control.value) ? null : { eInvoiceAddress: { message: 'ERROR.FORMAT' } };
  }

  private _emailAsyncValidator = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) return of(null);
    return this._dataService.emailTakenCheck(control.value, this.dataContext.id ?? null).pipe(
      map(res => {
        if (res) {
          return { emailExists: { message: 'CONTACT.VALIDATION.EMAIL_TAKEN' } }; // Angular error object
        }

        return null;
      })
    );
  };

  protected getFormGroup(): FormGroup {
    const form = this.fb.group({
      category: this._cf.createMandatory('TENANT.CATEGORY', this.dataContext.category),
      email: this._cf.createMaxLength('TENANT.EMAIL', this.dataContext.email, 255),
      emailInvoicing: this._cf.createMaxLength('COMMON.EMAIL_INVOICING', this.dataContext.emailInvoicing, 255),
      name: this._cf.createWithOptions('TENANT.NAME', this.dataContext.name, { required: true, length: 100 }),
      phone: this._cf.createMaxLength('TENANT.PHONE', this.dataContext.phone, 20),
      referenceName: this._cf.createMaxLength('TENANT.REFERENCE_NAME', this.dataContext.referenceName, 100),
      vatNumber: this._cf.createMaxLength('COMPANY.VAT_NUMBER', this.dataContext.vatNumber, 20),
      note: this._cf.createMaxLength('COMMON.NOTE', this.dataContext.note, 1000),
      invoiceAddress: this.fb.group({}),
      idType: this._cf.create('TENANT.ID_TYPE.ID_TYPE', this.dataContext.idType),
      contactIdentification: this._cf.create('TENANT.ID_TYPE.ID_NUMBER', this.dataContext.contactIdentification),
      eInvoiceAddress: this._cf.create('TENANT.EINVOICE_ADDRESS', this.dataContext.eInvoiceAddress),
      eInvoiceSpecified: this._cf.create(
        'TENANT.EINVOICE_SPECIFIED',
        this.dataContext.eInvoiceSpecified && this.dataContext.category === TenantCategory.Company
      ),
      b2cToggleControl: this._cf.create('TENANT.EINVOICE_B2C', this.b2cToggleDefaultValue),
      noJointDebiting: this._cf.create('TENANT.NO_JOINT_DEBITING', this.dataContext.noJointDebiting),

      applyCreditOnInvoices: this._cf.create(
        'TENANT.APPLY_CREDIT_ON_INVOICES',
        this.dataContext.isNew ? this.dataContext.category === TenantCategory.PhysicalPerson : this.dataContext.applyCreditOnInvoices
      ),
      counterpart: this._cf.createMaxLength('COMMON.COUNTERPART', this.dataContext.counterpart, 10),
      webUserUserName: this._cf.createReadonly('COMPANY.DEBT_COLLECTION.USERNAME', this.dataContext.webUserUserName),
      webUserCreated: this._cf.createReadonly('COMMON.CREATED_DATE', dateTimeString(this.dataContext.webUserCreated)),
      webUserLastLogin: this._cf.createReadonly('RESOURCE.LAST_LOGIN', dateTimeString(this.dataContext.webUserLastLogin)),
    });

    this.setupFormB2B(form);
    this.setupFormB2C(form);
    this.setupFormEmailInvoicing(form);
    this.setupFormTenantName(form);
    this.setupFormDuplicateContactIdentification(form);
    this.setupFormB2CSuggestion(form);
    this.toggleApplyCreditOnInvoices(form);
    this.counterPartUsed = this.dataContext.useCounterpart;
    this.webUserExists = this.dataContext.webUserUserName && this.dataContext.webUserUserName.length > 0;

    return form;
  }

  private setupFormB2B(form: FormGroup): void {
    this.hasB2B = this.dataContext.eInvoiceSpecified && this.dataContext.category === TenantCategory.Company;
    this.AddSubscription(
      form.controls['eInvoiceSpecified'].valueChanges.subscribe(value => {
        this.hasB2B = value && this.isCompany;
        if (value) {
          if (!this.form.controls['eInvoiceAddress'].value) {
            let orgNo = this.form.controls['contactIdentification'].value;
            if (!orgNo) orgNo = '';
            orgNo = orgNo.replace('-', '');
            let prefix = '';
            switch (this.form.controls['idType'].value) {
              case ContactIdKind.DanishOrganizationNumber:
                prefix = '0096';
                break;
              case ContactIdKind.SwedishOrganizationNumber:
                prefix = '0007';
                break;
              case ContactIdKind.FinnishOrganizationNumber:
                prefix = '0037';
                break;
            }
            this.form.controls['eInvoiceAddress'].setValue(prefix + ':' + orgNo);
          }
          this.form.controls['eInvoiceAddress'].setValidators([Validators.required, this.eInvoiceAddress]);
        } else {
          this.form.controls['eInvoiceAddress'].setValue(null);
          this.form.controls['eInvoiceAddress'].clearValidators();
        }
        this.form.controls['eInvoiceAddress'].updateValueAndValidity();
      })
    );

    this.AddSubscription(
      form.controls['category'].valueChanges.subscribe(value => {
        if (this.hasB2B) this.form.controls['eInvoiceAddress'].setValidators([Validators.required, this.eInvoiceAddress]);
        else this.form.controls['eInvoiceAddress'].clearValidators();
        this.form.controls['eInvoiceAddress'].updateValueAndValidity();
      })
    );
    if (this.hasB2B) {
      form.controls['eInvoiceAddress'].setValidators([Validators.required, this.eInvoiceAddress]);
    }
  }

  private setupFormB2C(form: FormGroup): void {
    this.hasToggledB2C = false;
    this.AddSubscription(
      form.controls['b2cToggleControl'].valueChanges.subscribe((val: boolean) => {
        if (!val === this.b2cToggleDefaultValue) {
          this.hasToggledB2C = true;
        }
      })
    );
  }

  private setupFormEmailInvoicing(form: FormGroup): void {
    this.emailInvoicing = this.dataContext.emailInvoicing;
    this.AddSubscription(
      form.controls['email'].valueChanges.subscribe(value => {
        if (value) {
          if (!this.emailInvoicing || this.emailInvoicing === '') this.form.controls['emailInvoicing'].setValue(value);
        }
      })
    );
  }

  private setupFormTenantName(form: FormGroup): void {
    this.AddSubscription(
      form.controls['name'].valueChanges.pipe(startWith(null), pairwise()).subscribe(([prev, next]: [string, string]) => {
        const currentAddress = this.form.controls['invoiceAddress'].value;
        if (currentAddress && (!currentAddress.name || prev === currentAddress.name)) {
          const nameControl = (this.form.controls['invoiceAddress'] as FormGroup).controls['name'];
          if (nameControl) nameControl.setValue(next);
        }
      })
    );
  }

  private setupFormDuplicateContactIdentification(form: FormGroup): void {
    const control = form.controls['contactIdentification'];

    control.addAsyncValidators(currentControl => {
      const contactIdentification = currentControl.value as string;
      this.existingContactName = null;

      if (!contactIdentification || contactIdentification.length <= 10) {
        return of(null);
      }

      // Keep the form pending until the duplicate lookup has completed. Previously the
      // detached subscription allowed a new contact to be saved before the response arrived.
      return timer(250).pipe(
        switchMap(() => this._dataService.getNameByCivicOrOrgNo(this.dataContext.id, contactIdentification)),
        tap(name => (this.existingContactName = name)),
        map(() => null),
        catchError(() => {
          this.existingContactName = null;
          return of(null);
        })
      );
    });
  }

  private setupFormB2CSuggestion(form: FormGroup): void {
    this.AddSubscription(
      form.controls['contactIdentification'].valueChanges
        .pipe(
          switchMap((val: string) => {
            this.b2cSuggestions = [];
            this.b2CSubscriptionsSuggestionText = '';
            if (
              val &&
              val.length == 13 &&
              this.isPerson &&
              !this.isMappedToB2C &&
              (this.idType === ContactIdKind.SwedishSSN || this.idType === ContactIdKind.FreeText)
            ) {
              return this._b2cContactService.getSuggestions(val, this.dataContext.id);
            }
            return of([]);
          })
        )
        .subscribe((suggestions: B2CSubscriptionSuggestion[]) => {
          this.b2cSuggestions = suggestions;
          const distinctRecipientIdentities = suggestions.map(x => x.identity.recipient).filter((x, i, array) => array.indexOf(x) == i);
          this.b2CSubscriptionsSuggestionText = distinctRecipientIdentities.join(', ');
        })
    );
  }

  private toggleApplyCreditOnInvoices(form: FormGroup): void {
    form.controls['category'].valueChanges.pipe(startWith(null), pairwise()).subscribe(([prev, curr]: [TenantCategory, TenantCategory]) => {
      // Only change the value if the category actually changed (not on initial load)
      if (prev !== null && prev !== curr) {
        form.controls['applyCreditOnInvoices'].setValue(curr === TenantCategory.PhysicalPerson);
      }
    });
  }

  get contactIdentificationDoesNotMatchB2CIdentity(): false | { sweSSN: string; b2cSSN: string } {
    if (this.isMappedToB2C && this.idType === ContactIdKind.SwedishSSN) {
      let sweSSN = this.form.controls['contactIdentification'].value;
      if (sweSSN) {
        sweSSN = sweSSN.replace(/\D/g, ''); // remove non-digits
        const b2cSSN = this.b2cSubscriptionContacts[0].getSSN();
        if (sweSSN != b2cSSN) {
          console.warn(`Contact identification '${sweSSN}' does not match B2C recipient identity '${b2cSSN}'`);
          return { sweSSN, b2cSSN };
        }
      }
    }
    return false;
  }

  protected createModel(): Contact {
    let model = new Contact();
    return model;
  }

  protected prepareSave(): Contact {
    let model = super.prepareSave();
    model.invoiceAddress = this.form.controls['invoiceAddress'].value;

    if (this.hasToggledB2C && this.isPerson) {
      const b2cToggleControl = this.b2cToggleControl; // TODO-B2C: replace toggle with button..?
      if (b2cToggleControl) {
        b2cToggleControl.isReadonly = true;

        if (b2cToggleControl.value === true) {
          if (this.isMappedToB2C) {
            model.b2cSaveAction = B2CContactSaveAction.EnableAll;
          } else {
            model.b2cSaveAction = B2CContactSaveAction.AcceptAndEnableAll;
          }
        } else if (b2cToggleControl.value === false) {
          if (this.isMappedToB2C) {
            model.b2cSaveAction = B2CContactSaveAction.DisableAll;
            this.hasActiveB2C = false;
          }
        }
      }
    }

    return model;
  }

  protected /*override*/ formSubmitted() {
    this.hasToggledB2C = false;
    super.formSubmitted();
  }

  protected /*override*/ formSubmittedChange(response: IB2CContactToggleResult[]) {
    if (this.b2cToggleControl) {
      this._b2cSaveResultHandler(response, this.b2cToggleControl);
      this._getB2CSubscriptions(this.b2cToggleControl); // this handles b2cToggleControl.isReadonly
    }
    super.formSubmittedChange();
  }

  protected /*override*/ formSubmittedWithError() {
    if (this.b2cToggleControl) {
      this.b2cToggleControl.isReadonly = false;
    }
    super.formSubmittedWithError();
  }

  private waitingForDuplicateCheckBeforeSubmit = false;

  onSubmit() {
    if (this.form?.pending) {
      if (!this.waitingForDuplicateCheckBeforeSubmit) {
        this.waitingForDuplicateCheckBeforeSubmit = true;
        this.AddSubscription(
          this.form.statusChanges
            .pipe(
              filter(status => status !== 'PENDING'),
              take(1)
            )
            .subscribe(() => {
              this.waitingForDuplicateCheckBeforeSubmit = false;
              this.onSubmit();
            })
        );
      }
      return;
    }

    if (!this._allowDuplicateSSN && this.existingContactName && this.form.controls['idType'].value === ContactIdKind.SwedishSSN) {
      return;
    }

    const mismatchSSN = this.contactIdentificationDoesNotMatchB2CIdentity;
    if (mismatchSSN) {
      // TODO-B2C: add to validation instead ?
      this.messageService.showDialog(`TENANT.MESSAGE.B2C_SSN_MISMATCH`, {
        ssn: formatPersonalIdNo(mismatchSSN.sweSSN),
        ssnb2c: formatPersonalIdNo(mismatchSSN.b2cSSN),
        hideCancel: true,
      });
      return;
    }

    const hasMappedB2C = !!this.b2cSubscriptionContacts?.length;
    if (hasMappedB2C && !this.isPerson) {
      // TODO-B2C: add to validation instead ?
      this.messageService.showDialog(`TENANT.MESSAGE.B2C_CANNOT_BE_COMPANY`, {
        hideCancel: true,
      });
      return;
    }

    if (this._verifyEmailDuplicates) {
      this._emailAsyncValidator(this.form.controls['email']).subscribe(error => {
        if (error) {
          this.form.controls['email'].setErrors(error);
          return;
        } else {
          this.form.controls['email'].setErrors(null);
        }

        super.onSubmit();
      });
    } else {
      super.onSubmit();
    }
  }

  private _b2cSaveResultHandler(toggleResults: IB2CContactToggleResult[], b2cToggleControl: FormControlEx) {
    if (!Array.isArray(toggleResults) || !toggleResults.length || !toggleResults[0].leaseAgreementId) {
      return; // either there were no changes or argument is of the wrong type.
    }
    // update delivery methods on lease agreements
    const agreements = this.dataContext.leaseAgreements;
    for (const r of toggleResults) {
      const a = agreements.find(x => x.id === r.leaseAgreementId);
      if (a) {
        a.invoiceDeliveryMethod = r.deliveryMethod;
        this.AddSubscription(
          this._languageService.translate(InvoiceDeliveryMethodToString(r.deliveryMethod)).subscribe((txt: string) => {
            a.invoiceDeliveryMethodText = txt;
          })
        );
      }
    }
    this.AddSubscription(
      this._languageService.translate('TENANT.TOGGLED_B2C_RESULT', { count: toggleResults.length }).subscribe((txt: string) => {
        //msgService.showSuccess(txt);  // TODO-B2C: MessageService can only show one message at a time... (it can show multiple in Market - so time to upgrade??)
        console.log(txt);
      })
    );
  }

  protected init(tenant: Contact) {
    if (this.protectedIdentitySidebarContactId && this.protectedIdentitySidebarContactId !== tenant.id) {
      this.closeProtectedIdentitySidebar();
    }
    this.addDeleteMenu = false;
    super.init(tenant);
    this.syncProtectedIdentityMenu();

    this._getConfig();
    this._getIdTypes(tenant);
    this.setupCategoryIdTypesChanges();
    this._getB2CSubscriptions(this.b2cToggleControl);
  }

  private syncProtectedIdentityMenu(): void {
    this.menuDefinition = this.menuDefinition.filter(item => item.commandId !== tenantCommand.protectedIdentity);
    if (this.hasProtectedIdentityFeature && this.isExistingContact && this.isPerson) {
      const showInvoicesIndex = this.menuDefinition.findIndex(item => item.commandId === tenantCommand.showInvoices);
      const insertIndex = showInvoicesIndex >= 0 ? showInvoicesIndex + 1 : this.menuDefinition.length;
      this.menuDefinition.splice(
        insertIndex,
        0,
        new MenuItemDefinition('PROTECTED_IDENTITY.MENU_ITEM', tenantCommand.protectedIdentity)
      );
      return;
    }

    if (!this.hasProtectedIdentityFeature || !this.isPerson) {
      this.sidebarService.toggleSidebar(false);
    }
  }

  private get isExistingContact(): boolean {
    return !!this.dataContext?.id || !!this.activatedRoute.snapshot.paramMap.get('id');
  }

  private _getConfig(): void {
    this.AddSubscription(
      this._profileService.getConfig().subscribe(res => {
        this._allowDuplicateSSN = res.allowDuplicateSwedishSSN;
        this._verifyEmailDuplicates = res.enableUserFeatures;
      })
    );
  }

  private _getIdTypes(tenant: Contact): void {
    // First set the current value (and lock the control):
    const idType = new IdType(tenant.category, tenant.idType);
    this.idTypeSelection = [new SelectionItem(idType.caption, idType.idType)];
    (this.form.controls['idType'] as FormControlEx).isReadonly = true;

    // Then load all the available options (and unlock the control):
    this.AddSubscription(
      this._dataService.getIdTypes().subscribe(types => {
        this._idTypes = types;
        this.idTypeSelection = this._idTypes
          .filter(i => i.category === this.dataContext.category)
          .map(i => new SelectionItem(i.caption, i.idType));
        if (this.isNew) {
          this.form.controls['idType'].setValue(this.idTypeSelection[0].value);
        }
        (this.form.controls['idType'] as FormControlEx).isReadonly = false;
      })
    );
  }

  private setupCategoryIdTypesChanges(): void {
    this.AddSubscription(
      this.categoryControl.valueChanges.subscribe((value: TenantCategory) => {
        if (this._idTypes) {
          this.idTypeSelection = this._idTypes.filter(i => i.category === value).map(i => new SelectionItem(i.caption, i.idType));
          if (!this.idTypeSelection.find(s => s.value == this.form.controls['idType'].value)) {
            this.form.controls['idType'].setValue(this.idTypeSelection[0].value);
          }
        }
        if (!this.isPerson) {
          this.b2cSuggestions = [];
          this.b2CSubscriptionsSuggestionText = '';
        }
        this.syncProtectedIdentityMenu();
      })
    );
    this.categoryControl.updateValueAndValidity();
  }

  private _getB2CSubscriptions(b2cToggleControl: FormControlEx): void {
    if (!this.isPerson || !this.dataContext.id) {
      return;
    }
    if (b2cToggleControl) {
      b2cToggleControl.isReadonly = true;
    }
    if (this.categoryControl) {
      this.categoryControl.isReadonly = true;
    }
    this.AddSubscription(
      this._b2cContactService.getForContact(this.dataContext.id).subscribe(subContacts => {
        subContacts ??= [];
        this.b2cSubscriptionContacts = subContacts;
        const distinctRecipientIdentities = subContacts
          .map(x => x.subscription.identity.recipient)
          .filter((x, i, array) => array.indexOf(x) == i);
        this.b2CSubscriptionsHasText = distinctRecipientIdentities.join(', ');
        this.hasActiveB2C = subContacts.some(x => x.isEnabled && x.subscription.isActive);

        if (this.categoryControl) {
          this.categoryControl.isReadonly = false;
        }
        if (b2cToggleControl) {
          b2cToggleControl.setValue(this.b2cToggleDefaultValue, { emitEvent: false });
          b2cToggleControl.isReadonly = false;
        }
      })
    );
  }

  onRegistrationCodeClicked() {
    this.showCode = !this.showCode;
  }

  private _getPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onApplicationRowClicked(e: { context: RentApplication; event: any }): void {
    this.navigation.navigate(['rent-application', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onCopyToClipboard() {
    this._clipboard.copy(this.dataContext.registrationKey);
    this._messageService.showMessage('REPRESENTATIVE.FORM.COPY_TO_CLIPBOARD_MESSAGE');
  }
}
